import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { userPool } from './cognito';
import { exchangeCodeForTokens, startGoogleSignIn } from './google';
import {
  getSession,
  hydrateFromTokens,
  profileFromSession,
  signOutCurrent,
  type Profile,
} from './session';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthValue {
  readonly status: Status;
  readonly profile: Profile | null;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, name: string): Promise<void>;
  confirm(email: string, code: string): Promise<void>;
  resendCode(email: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  completeGoogleSignIn(code: string): Promise<void>;
  signOut(): void;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [profile, setProfile] = useState<Profile | null>(null);

  const applySession = useCallback((session: CognitoUserSession | null) => {
    if (session) {
      setProfile(profileFromSession(session));
      setStatus('authenticated');
    } else {
      setProfile(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void getSession().then(applySession);
  }, [applySession]);

  const signIn = useCallback(
    (email: string, password: string) =>
      new Promise<void>((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
          onSuccess: (session) => {
            applySession(session);
            resolve();
          },
          onFailure: reject,
        });
      }),
    [applySession],
  );

  const signUp = useCallback(
    (email: string, password: string, name: string) =>
      new Promise<void>((resolve, reject) => {
        const attrs = [
          new CognitoUserAttribute({ Name: 'email', Value: email }),
          new CognitoUserAttribute({ Name: 'name', Value: name }),
        ];
        userPool.signUp(email, password, attrs, [], (err) => (err ? reject(err) : resolve()));
      }),
    [],
  );

  const confirm = useCallback(
    (email: string, code: string) =>
      new Promise<void>((resolve, reject) => {
        new CognitoUser({ Username: email, Pool: userPool }).confirmRegistration(
          code,
          true,
          (err) => (err ? reject(err) : resolve()),
        );
      }),
    [],
  );

  const resendCode = useCallback(
    (email: string) =>
      new Promise<void>((resolve, reject) => {
        new CognitoUser({ Username: email, Pool: userPool }).resendConfirmationCode((err) =>
          err ? reject(err) : resolve(),
        );
      }),
    [],
  );

  const completeGoogleSignIn = useCallback(async (code: string) => {
    setProfile(hydrateFromTokens(await exchangeCodeForTokens(code)));
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(() => {
    signOutCurrent();
    setProfile(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      profile,
      signIn,
      signUp,
      confirm,
      resendCode,
      signInWithGoogle: startGoogleSignIn,
      completeGoogleSignIn,
      signOut,
    }),
    [status, profile, signIn, signUp, confirm, resendCode, completeGoogleSignIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
